import { ArrowRightIcon, CheckIcon } from "@heroicons/react/24/solid";
import { tinaField, useTina } from "tinacms/dist/react";
import ButtonPrimary from "../ui-elements/ButtonPrimary";
import content from "../../../content/pages/matrix-drops-protocol.json";
import { PageDocument } from "../../../tina/__generated__/types";
import { PageSeo } from "../../navigation/PageSeo";
import "./MatrixDropsProtocolPage.css";

const initialData = { page: content };
const variables = { relativePath: "matrix-drops-protocol.json" };

export const MatrixDropsProtocol = () => {
  const { data } = useTina({ query: PageDocument, variables, data: initialData });
  const { hero, pillarsSection, kma, geopathic, diet } = data.page;

  return (
    <main className="matrix-protocol-page">
      <PageSeo pathname="/matrix-drops-protocol" seo={data.page.seo} />
      <section className="matrix-protocol-hero page-hero-dots">
        <div>
          <p className="matrix-protocol-eyebrow" data-tina-field={tinaField(hero, "eyebrow")}>
            {hero.eyebrow}
          </p>
          <h1 data-tina-field={tinaField(hero, "title")}>
            {hero.title} <span>{hero.titleHighlight}</span>
          </h1>
          <p className="matrix-protocol-intro" data-tina-field={tinaField(hero, "intro")}>
            {hero.intro}
          </p>
          <ButtonPrimary url={hero.ctaHref ?? "/booking"} label={hero.ctaLabel ?? ""} size="md" />
        </div>
        <div className="matrix-protocol-art">
          <img
            src={hero.imageSrc ?? undefined}
            alt={hero.imageAlt ?? ""}
            data-tina-field={tinaField(hero, "imageSrc")}
          />
        </div>
      </section>

      <section className="matrix-protocol-section" aria-labelledby="matrix-protocol-pillars-title">
        <p className="matrix-protocol-eyebrow" data-tina-field={tinaField(pillarsSection, "eyebrow")}>
          {pillarsSection.eyebrow}
        </p>
        <h2 id="matrix-protocol-pillars-title" data-tina-field={tinaField(pillarsSection, "title")}>
          {pillarsSection.title}
        </h2>
        <p className="matrix-protocol-section-intro" data-tina-field={tinaField(pillarsSection, "intro")}>
          {pillarsSection.intro}
        </p>
        <ol className="matrix-protocol-pillars">
          {(pillarsSection.pillars ?? []).map((pillar, index) => (
            <li className="matrix-protocol-pillar" key={index} data-tina-field={tinaField(pillar)}>
              <span className="matrix-protocol-number">{index + 1}</span>
              <div>
                <h3>{pillar.title}</h3>
                <p>{pillar.text}</p>
              </div>
            </li>
          ))}
        </ol>
        <p className="matrix-protocol-note" data-tina-field={tinaField(pillarsSection, "note")}>
          {pillarsSection.note}
        </p>
      </section>

      <section className="matrix-protocol-section matrix-kma-description" aria-labelledby="matrix-kma-title">
        <p className="matrix-protocol-eyebrow" data-tina-field={tinaField(kma, "eyebrow")}>
          {kma.eyebrow}
        </p>
        <h2 className="matrix-description-heading" id="matrix-kma-title" data-tina-field={tinaField(kma, "title")}>
          <span className="matrix-protocol-number">{kma.number}</span>
          <span>{kma.title}</span>
        </h2>
        <p data-tina-field={tinaField(kma, "text")}>{kma.text}</p>
        <a
          className="matrix-kma-link"
          href={kma.linkHref ?? "/massage-services"}
          data-tina-field={tinaField(kma, "linkLabel")}
        >
          {kma.linkLabel}
        </a>
      </section>

      <section className="matrix-protocol-section matrix-geopathic-section" aria-labelledby="matrix-geopathic-title">
        <p className="matrix-protocol-eyebrow" data-tina-field={tinaField(geopathic, "eyebrow")}>
          {geopathic.eyebrow}
        </p>
        <h2
          className="matrix-description-heading"
          id="matrix-geopathic-title"
          data-tina-field={tinaField(geopathic, "title")}
        >
          <span className="matrix-protocol-number">{geopathic.number}</span>
          <span>{geopathic.title}</span>
        </h2>
        <div className="matrix-geopathic-content">
          {(geopathic.subsections ?? []).map((subsection, index) => (
            <section
              className={`matrix-geopathic-subsection ${
                index < 3 ? "matrix-geopathic-overview" : "matrix-geopathic-card"
              }${subsection.imageSrc && index >= 3 ? " matrix-geopathic-subsection-with-image" : ""}`}
              key={index}
              data-tina-field={tinaField(subsection)}
            >
              <div>
                <h3>{subsection.title}</h3>
                <div className={index >= 3 ? "matrix-geopathic-card-list" : undefined}>
                  {(subsection.paragraphs ?? []).map((paragraph, paragraphIndex) => (
                    <p key={paragraphIndex}>{paragraph}</p>
                  ))}
                </div>
              </div>
              {subsection.imageSrc && <img src={subsection.imageSrc} alt={subsection.imageAlt ?? ""} />}
            </section>
          ))}
        </div>
      </section>

      <section className="matrix-protocol-section matrix-diet-section" aria-labelledby="matrix-diet-title">
        <p className="matrix-protocol-eyebrow" data-tina-field={tinaField(diet, "eyebrow")}>
          {diet.eyebrow}
        </p>
        <h2 id="matrix-diet-title" className="matrix-description-heading">
          <span className="matrix-protocol-number" data-tina-field={tinaField(diet, "number")}>
            {diet.number}
          </span>
          <span data-tina-field={tinaField(diet, "title")}>{diet.title}</span>
        </h2>
        <div className="matrix-diet-inner">
          <div className="matrix-diet-columns">
            <article className="matrix-diet-column">
              <h3 data-tina-field={tinaField(diet.avoid, "title")}>{diet.avoid.title}</h3>
              <ul>
                {(diet.avoid.foods ?? []).map((food) => (
                  <li key={food}>
                    <span className="matrix-diet-marker" aria-hidden="true">
                      <ArrowRightIcon />
                    </span>
                    {food}
                  </li>
                ))}
              </ul>
              <img
                src={diet.avoid.imageSrc ?? undefined}
                alt={diet.avoid.imageAlt ?? ""}
                loading="lazy"
                data-tina-field={tinaField(diet.avoid, "imageSrc")}
              />
            </article>
            <article className="matrix-diet-column">
              <h3 data-tina-field={tinaField(diet.recommended, "title")}>{diet.recommended.title}</h3>
              <ul>
                {(diet.recommended.foods ?? []).map((food) => (
                  <li key={food}>
                    <span className="matrix-diet-marker" aria-hidden="true">
                      <CheckIcon />
                    </span>
                    {food}
                  </li>
                ))}
              </ul>
              <img
                src={diet.recommended.imageSrc ?? undefined}
                alt={diet.recommended.imageAlt ?? ""}
                loading="lazy"
                data-tina-field={tinaField(diet.recommended, "imageSrc")}
              />
            </article>
          </div>
          <p className="matrix-diet-note" data-tina-field={tinaField(diet, "note")}>
            {diet.note}
          </p>
        </div>
      </section>

      {(pillarsSection.pillars ?? []).slice(3).map((pillar, index) => {
        const number = index + 4;
        const titleId = `matrix-pillar-${number}-title`;
        const images = (pillar.images ?? []).filter((image) => image.imageSrc);

        return (
          <section
            className="matrix-protocol-section matrix-additional-pillar"
            key={titleId}
            aria-labelledby={titleId}
            data-tina-field={tinaField(pillar)}
          >
            <div className="matrix-protocol-eyebrow">Pillar {number}</div>
            <h2 className="matrix-description-heading" id={titleId} data-tina-field={tinaField(pillar, "title")}>
              <span className="matrix-protocol-number">{number}</span>
              <span>{pillar.title}</span>
            </h2>
            <p data-tina-field={tinaField(pillar, "text")}>{pillar.text}</p>

            {number === 4 && (
              <p className="text-center w-full m-auto pt-6">
                If you feel the presence of disturbing entities in your environment or your toughts are not your own, it
                is important to address them appropriately. With the Matrix Drops Computer (MDC) we can mesure the aura
                and detect disturbances, shadow personalities, also known as entities, which are spirits attached to
                people. In these cases, the MDC recommends a special Matrix Drops to keep the entity away. You need to
                take 20 drops once a week from the proper Matrix Drops to solve the issue. With MDC we can detect
                external blocking effects and curses. These can have negative effects on us, and the MDC helps to
                identify them so we can take the necessary steps to remove or neutralize them.
              </p>
            )}
            {number === 5 && (
              <p className="text-center w-full m-auto pt-6">
                Electrosmog is the electromagnetic radiation emitted by electrical appliances. The human body has not
                been subjected to such a serious degree of electrosmog load a few decades ago. The number of the set up
                transmission towers and radiant electrical equipment is radically growing in our world, in parallel the
                amount of disharmonious force fields increased. As a result, the electromagnetic radiation exposure
                increased more than 300 times on average. Recent summaries suggest that the values are even more
                daunting for 5G. The growing concern made the necessity of protection against the harmful radiation even
                more justified and indispensable. Electrosmog keeps the body in a state of constant stress, which
                continuously increases the tension and irritability in all living beings. As the stress increases, the
                immune system strength decreases. Radiation is not perceptible directly to our senses, but it is
                ubiquitous in our environment in the form of basic or background radiation.
              </p>
            )}
            {number === 5 && images.length > 0 && (
              <div className="matrix-pillar-images" aria-label="Electrosmog protection devices">
                {images.map((image, imageIndex) => (
                  <img
                    key={imageIndex}
                    src={image.imageSrc ?? undefined}
                    alt={image.imageAlt ?? ""}
                    loading="lazy"
                    data-tina-field={tinaField(image, "imageSrc")}
                  />
                ))}
              </div>
            )}
            {number === 6 && images.length > 0 && (
              <div className="matrix-pillar-images" aria-label="Matrix pillar 6 images">
                {images.map((image, imageIndex) => (
                  <img
                    key={imageIndex}
                    src={image.imageSrc ?? undefined}
                    alt={image.imageAlt ?? ""}
                    loading="lazy"
                    data-tina-field={tinaField(image, "imageSrc")}
                  />
                ))}
              </div>
            )}
            {number === 7 && images.length > 0 && (
              <div>
                <div className="matrix-pillar-images text-center" aria-label="Matrix pillar 7 images">
                  {images.map((image, imageIndex) => (
                    <img
                      key={imageIndex}
                      src={image.imageSrc ?? undefined}
                      alt={image.imageAlt ?? ""}
                      loading="lazy"
                      data-tina-field={tinaField(image, "imageSrc")}
                    />
                  ))}
                </div>
                <div className="matrix-kma-link-container text-center m-auto w-full">
                  <a className="matrix-kma-link" href={"/matrix-drops"}>
                    Learn More about Matrix Drops
                  </a>
                </div>
              </div>
            )}
          </section>
        );
      })}
      <section>
        <ButtonPrimary
          className="matrix-booking-button m-4 p-4"
          url="/booking"
          label="Start your journey Book an Appointment"
          size="md"
        />
      </section>
    </main>
  );
};

export default MatrixDropsProtocol;
